import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import ApiServices from '../Services/ApiServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faBook,
    faGraduationCap,
    faStar,
    faEye,
    faMoneyBill,
    faPercent,
    faCalendarAlt,
    faLayerGroup,
    faArrowLeft,
    faArrowRight,
    faComment,
    faUser,
    faPaperPlane,
    faTrash,
    faSpinner,
    faUsers,
    faPlay,
    faShare,
    faCheckCircle,
    faInfoCircle,
    faShieldAlt,
    faTrophy,
    faCertificate,
    faChevronLeft,
    faChevronUp,
    faChevronDown,
    faQuoteRight
} from '@fortawesome/free-solid-svg-icons';
import { toastHelper } from '../../Helpers/toastHelper';
import { storageHelper } from '../../Helpers/storageHelper';
import Loader from '../Shared/Loader';
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import { motion, AnimatePresence } from 'framer-motion';
import DynamicModal from '../Shared/DynamicModal';
import VideoViewerV2 from '../Shared/VideoViewerV2';
import * as Yup from 'yup';
import htmlHelper from '../../Helpers/htmlHelper';


const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

const WebsiteCourseContent = ({ courseId: propCourseId } = {}) => {
    const { id: paramId } = useParams();
    const id = propCourseId || paramId;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isPreview =
        searchParams.get('preview') === 'true' ||
        searchParams.get('isLivePreview') === 'true' ||
        window.location.search.includes('preview=true') ||
        (typeof window !== 'undefined' && window.self !== window.top);
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [deletingCommentId, setDeletingCommentId] = useState(null);
    const [expandedVideoIndex, setExpandedVideoIndex] = useState(null);
    const [showFreeSubscribeModal, setShowFreeSubscribeModal] = useState(false);
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [registering, setRegistering] = useState(false);

    const freeSubscribeFormConfig = useMemo(() => {
        return [
            {
                name: 'student_name',
                label: 'اسم الطالب',
                type: 'text',
                required: true,
                validation: Yup.string()
                    .matches(/^[\u0600-\u06FF\s]+$|^[a-zA-Z\s]+$/, 'يجب أن يحتوي الاسم على أحرف فقط')
                    .required('اسم الطالب مطلوب'),
            },
            {
                name: 'student_email',
                label: 'البريد الإلكتروني',
                type: 'email',
                required: true,
                requiredMessage: 'البريد الإلكتروني مطلوب',
            },
            {
                name: 'student_phone',
                label: 'رقم الهاتف',
                type: 'tel',
                required: true,
                validation: Yup.string()
                    .matches(/^01[0-9]{9}$/, 'رقم هاتف غير صحيح، يجب أن يبدأ بـ 01 ويتكون من 11 رقماً')
                    .required('رقم الهاتف مطلوب'),
            },
            {
                name: 'gender',
                label: 'الجنس',
                type: 'select',
                required: true,
                requiredMessage: 'تحديد الجنس مطلوب',
                options: [
                    { value: '1', label: 'ذكر' },
                    { value: '2', label: 'أنثى' },
                ],
            },
            {
                name: 'password',
                label: 'كلمة المرور',
                type: 'password',
                required: true,
                requiredMessage: 'كلمة المرور مطلوبة',
                validation: Yup.string()
                    .min(6, 'كلمة المرور يجب أن لا تقل عن 6 أحرف')
                    .required('كلمة المرور مطلوبة'),
            },
            {
                name: 'confirm_password',
                label: 'تأكيد كلمة المرور',
                type: 'password',
                required: true,
                requiredMessage: 'تأكيد كلمة المرور مطلوب',
                validation: Yup.string()
                    .oneOf([Yup.ref('password'), null], 'كلمات المرور غير متطابقة')
                    .required('تأكيد كلمة المرور مطلوب'),
            },
        ];
    }, []);

    const handleFreeSubscribeSubmit = async (values) => {
        const payload = {
            course_id: id,
            student_name: values.student_name,
            student_email: values.student_email,
            student_phone: values.student_phone,
            gender: parseInt(values.gender),
            password: values.password,
            confirm_password: values.confirm_password,
        };

        try {
            const response = await ApiServices.post('CourseSubscription/SubscribeFreeCourseForUserNotBelongToPlatform', payload);
            toastHelper.success('تم التسجيل والاشتراك في الدورة بنجاح');

            if (response && response.check && response.data) {
                if (response.data.token) {
                    storageHelper.setItem('token', response.data.token);
                }
                if (response.data.user) {
                    storageHelper.setItem('user', JSON.stringify(response.data.user));
                    storageHelper.setItem('user_id', JSON.stringify(response.data.user.id));
                }
            }

            navigate('/mycourses');
        } catch (err) {
            toastHelper.error(err.msg || err.message || 'حدث خطأ أثناء التسجيل');
            throw err;
        }
    };

    const viewedCourseIdRef = useRef(null);

    useEffect(() => {
        if (!id) return;
        const fetchCourse = async () => {
            try {
                setLoading(true);
                const response = await ApiServices.get(`Course/Website/getById/${id}`);

                setCourse(response.data);

                if (viewedCourseIdRef.current !== id) {
                    viewedCourseIdRef.current = id;
                    try { await ApiServices.put(`Course/updateView/${id}`); } catch (e) { }
                }
            } catch (err) {
                setError('حدث خطأ أثناء تحميل تفاصيل الدورة');
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [id]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        const token = storageHelper.getItem('token');
        if (!token) return toastHelper.error('يجب عليك تسجيل الدخول أولاً');
        if (!comment.trim()) return toastHelper.error('يرجى كتابة تعليق');
        try {
            setSubmitting(true);
            const response = await ApiServices.post('CourseComment/create', { course_id: id, comment: comment.trim() });
            if (response.check) {
                toastHelper.success('تم إضافة التعليق بنجاح');
                setComment('');
                const courseResponse = await ApiServices.get(`Course/Website/getById/${id}`);
                if (courseResponse.check) setCourse(courseResponse.data);
            }
        } catch (err) { toastHelper.error('حدث خطأ أثناء إضافة التعليق'); } finally { setSubmitting(false); }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            setDeletingCommentId(commentId);
            const response = await ApiServices.delete(`CourseComment/delete/${commentId}`);
            if (response.check) {
                toastHelper.success('تم حذف التعليق بنجاح');
                const courseResponse = await ApiServices.get(`Course/Website/getById/${id}`);
                if (courseResponse.check) setCourse(courseResponse.data);
            }
        } catch (err) { toastHelper.error('حدث خطأ أثناء حذف التعليق'); } finally { setDeletingCommentId(null); }
    };

    const handleRegisterNow = async () => {
        const token = storageHelper.getItem('token');
        const isFree = Number(course?.price) === 0;

        if (isFree) {
            if (token) {
                setRegistering(true);
                try {
                    await ApiServices.post(`CourseSubscription/SubscribeFreeCourseForUserBelongToPlatform/${id}`);
                    toastHelper.success('تم الاشتراك في الدورة المجانية بنجاح');
                    navigate('/mycourses');
                } catch (err) {
                    toastHelper.error(err.msg || err.message || 'فشل في الاشتراك في الدورة');
                } finally {
                    setRegistering(false);
                }
            } else {
                setShowFreeSubscribeModal(true);
            }
        } else {
            if (!token) return toastHelper.error('يجب عليك تسجيل الدخول أولاً');
            setRegistering(true);
            try {
                await ApiServices.post(`CourseRequest/create/${id}`);
                toastHelper.success('تم إرسال طلب التسجيل بنجاح');
            } catch (err) {
                toastHelper.error(err.msg || 'فشل في إرسال طلب التسجيل');
            } finally {
                setRegistering(false);
            }
        }
    };

    const canDeleteComment = (comment) => {
        const currentUser = JSON.parse(storageHelper.getItem('user_id'));
        var userId = parseInt(currentUser);
        if (!userId) return false;
        return userId === course.instructor_id || userId === comment.comment_by_id;
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({ title: course?.name, url: window.location.href })
                .catch(() => {
                    navigator.clipboard?.writeText(window.location.href);
                });
        } else {
            navigator.clipboard?.writeText(window.location.href);
        }
    };

    if (loading) return <div className={`wcc-root ${isPreview ? 'wcc-preview-mode' : ''}`}>{!isPreview && <Header />}<div className="wcc-loader-box"><Loader /></div></div>;
    if (error || !course) return <div className={`wcc-root ${isPreview ? 'wcc-preview-mode' : ''}`}>{!isPreview && <Header />}<div className="wcc-error-box"><h3>{error || "الدورة غير موجودة"}</h3><button onClick={() => navigate('/')}>العودة للرئيسية</button></div></div>;

    return (
        <div className={`wcc-root ${isPreview ? 'wcc-preview-mode' : ''}`} dir="rtl">
            {!isPreview && <Header />}
            { }
            {/* Hero Section */}
            <section className="wcc-hero">
                <div className="wcc-hero-bg">
                    <img src={getFullFileUrl(course.course_cover)} alt={course.name} />
                    <div className="wcc-hero-overlay" />
                </div>
                <div className="wcc-container">
                    <motion.div className="wcc-hero-content" initial="hidden" animate="visible">
                        <motion.button className="wcc-btn-back" onClick={() => navigate('/')} variants={fadeUp} custom={0}>
                            <FontAwesomeIcon icon={faArrowRight} />
                            <span>العودة للرئيسية</span>
                        </motion.button>

                        {/* Big Center Play Button for Intro Video */}
                        {course.intro_video_url && (
                            <motion.div className="wcc-hero-play-wrapper" variants={fadeUp} custom={0.5}>
                                <button className="wcc-huge-play-btn" onClick={() => setShowIntroVideo(true)}>
                                    <div className="wcc-play-pulse"></div>
                                    <FontAwesomeIcon icon={faPlay} className="wcc-play-icon-inner" />
                                </button>
                                <span className="wcc-play-text">الفيديو التعريفي</span>
                            </motion.div>
                        )}

                        <motion.div className="wcc-cat-tag" variants={fadeUp} custom={1}>
                            <FontAwesomeIcon icon={faLayerGroup} />
                            <span>{course.category}</span>
                        </motion.div>

                        <motion.div className="wcc-hero-meta" variants={fadeUp} custom={2}>
                            <div className="wcc-meta-pill">
                                <FontAwesomeIcon icon={faGraduationCap} />
                                <span>{course.instructor || "منصة امتحاني"}</span>
                            </div>
                            <div className="wcc-meta-pill">
                                <FontAwesomeIcon icon={faClock} />
                                <span>{course.hours} ساعة تدريبية</span>
                            </div>
                            <div className="wcc-meta-pill">
                                <FontAwesomeIcon icon={faBook} />
                                <span>{course.material}</span>
                            </div>
                            <button className="wcc-btn-share-hero" onClick={handleShare}>
                                <FontAwesomeIcon icon={faShare} />
                            </button>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Main Content Area */}
            <div className="wcc-main-wrap">
                <div className="wcc-container">
                    <div className="wcc-grid">

                        {/* Right: Main Info */}
                        <div className="wcc-main-col">
                            {/* Course Title (Outside Cover Area) */}
                            <motion.div className="wcc-outside-title-box" variants={fadeUp} initial="hidden" animate="visible">
                                <h1 className="wcc-outside-title">{course.name}</h1>
                            </motion.div>

                            {/* Stats Bar */}
                            <motion.div className="wcc-stats-bar" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                <div className="wcc-stat-card">
                                    <div className="wcc-stat-icon is-gold"><FontAwesomeIcon icon={faStar} /></div>
                                    <div className="wcc-stat-info">
                                        <div className="val">{course.rate}</div>
                                        <div className="lbl">التقييم العام</div>
                                    </div>
                                </div>
                                <div className="wcc-stat-card">
                                    <div className="wcc-stat-icon is-indigo"><FontAwesomeIcon icon={faEye} /></div>
                                    <div className="wcc-stat-info">
                                        <div className="val">{course.views}</div>
                                        <div className="lbl">إجمالي المشاهدات</div>
                                    </div>
                                </div>
                                <div className="wcc-stat-card">
                                    <div className="wcc-stat-icon is-emerald"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                                    <div className="wcc-stat-info">
                                        <div className="val">{new Date(course.created_at).toLocaleDateString('ar-EG')}</div>
                                        <div className="lbl">تاريخ الإصدار</div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Description Section */}
                            <motion.section className="wcc-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                <div className="wcc-eyebrow"><span className="wcc-eyebrow-dot" />عن الدورة التدريبية</div>
                                <h2 className="wcc-sec-title">ماذا ستتعلم في <em>هذه الدورة؟</em></h2>
                                <div className="wcc-card-desc">
                                    <FontAwesomeIcon icon={faQuoteRight} className="wcc-quote-icon" />
                                    <div
                                        className="wcc-html-content"
                                        dangerouslySetInnerHTML={{ __html: course.description }}
                                    />
                                </div>
                            </motion.section>

                            {/* Curriculum Section */}
                            {course.course_video_content && course.course_video_content.length > 0 && (
                                <motion.section className="wcc-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                    <div className="wcc-eyebrow"><span className="wcc-eyebrow-dot" />محتوى الدورة</div>
                                    <h2 className="wcc-sec-title">دروس <em>ومحاضرات</em></h2>
                                    <div className="wcc-curriculum-list">
                                        {course.course_video_content.map((vid, idx) => (
                                            <div key={idx} className={`wcc-curriculum-item ${expandedVideoIndex === idx ? 'is-expanded' : ''}`}>
                                                <div className="wcc-curriculum-header" onClick={() => setExpandedVideoIndex(prev => prev === idx ? null : idx)}>
                                                    <div className="wcc-curriculum-num">{String(idx + 1).padStart(2, '0')}</div>
                                                    <div className="wcc-curriculum-info">
                                                        <h4 className="wcc-curriculum-title" style={{ margin: 0 }}>
                                                            {htmlHelper.stripHtml(vid.description)?.split(':')[0]?.length < 50 ? htmlHelper.stripHtml(vid.description)?.split(':')[0] : 'الدرس ' + (idx + 1)}
                                                        </h4>
                                                    </div>
                                                    <div className="wcc-curriculum-toggle">
                                                        <FontAwesomeIcon icon={expandedVideoIndex === idx ? faChevronUp : faChevronDown} style={{ color: '#94a3b8' }} />
                                                    </div>
                                                </div>
                                                <AnimatePresence>
                                                    {expandedVideoIndex === idx && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            style={{ overflow: 'hidden' }}
                                                        >
                                                            <div className="wcc-curriculum-body">
                                                                <div className="wcc-curriculum-desc" dangerouslySetInnerHTML={{ __html: vid.description }}></div>
                                                                <div className="wcc-curriculum-meta">
                                                                    <span className="wcc-meta-pill is-small">
                                                                        <FontAwesomeIcon icon={faCalendarAlt} /> {new Date(vid.created_at).toLocaleDateString('ar-EG')}
                                                                    </span>
                                                                    {vid.attachments && vid.attachments.length > 0 && (
                                                                        <span className="wcc-meta-pill is-small is-attachment">
                                                                            <FontAwesomeIcon icon={faLayerGroup} /> {vid.attachments.length} مرفقات
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        ))}
                                    </div>
                                </motion.section>
                            )}

                            {/* Comments Section */}
                            <motion.section className="wcc-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                <div className="wcc-eyebrow">آراء الطلاب</div>
                                <div className="wcc-sec-head">
                                    <h2 className="wcc-sec-title">تفاعل <em>المجتمع</em></h2>
                                    <div className="wcc-comments-count">{course.course_comments?.length || 0} تعليق</div>
                                </div>

                                <div className="wcc-comment-form-wrap">
                                    <form onSubmit={handleCommentSubmit} className="wcc-comment-form">
                                        <textarea
                                            value={comment} onChange={(e) => setComment(e.target.value)}
                                            placeholder="شاركنا رأيك في الدورة..." rows="3"
                                            disabled={isPreview}
                                        />
                                        <button type="submit" disabled={submitting || isPreview}>
                                            {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPaperPlane} /><span>إرسال التعليق</span></>}
                                        </button>
                                    </form>
                                </div>

                                <div className="wcc-comments-list">
                                    {(!course.course_comments || course.course_comments.length === 0) ? (
                                        <div className="wcc-empty-comments">
                                            <FontAwesomeIcon icon={faComment} />
                                            <p>لا توجد تعليقات بعد. كن أول من يشارك رأيه!</p>
                                        </div>
                                    ) : (
                                        course.course_comments.map((c, i) => (
                                            <motion.div key={c.id} className="wcc-comment-card" variants={fadeUp} custom={i % 5} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                                <div className="wcc-cmt-head">
                                                    <div className="wcc-user-info">
                                                        <div className="wcc-avatar">
                                                            {c.comment_by_image ? <img src={getFullFileUrl(c.comment_by_image)} alt={c.comment_by} /> : <FontAwesomeIcon icon={faUser} />}
                                                        </div>
                                                        <div className="wcc-user-meta">
                                                            <div className="name">{c.comment_by}</div>
                                                            <div className="date">{c.created_at}</div>
                                                        </div>
                                                    </div>
                                                    {canDeleteComment(c) && (
                                                        <button className="wcc-btn-del" onClick={() => handleDeleteComment(c.id)} disabled={deletingCommentId === c.id}>
                                                            {deletingCommentId === c.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="wcc-cmt-body"><p>{c.comment}</p></div>
                                            </motion.div>
                                        ))
                                    )}
                                </div>
                            </motion.section>
                        </div>

                        {/* Left: Sticky Sidebar */}
                        <aside className="wcc-sidebar-col">
                            <motion.div className="wcc-pricing-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
                                <div className="wcc-pricing-head">
                                    <h3>بيانات الاشتراك</h3>
                                    <div className="wcc-price-badge"><FontAwesomeIcon icon={faCertificate} /></div>
                                </div>

                                {Number(course.price) === 0 ? (
                                    <motion.div
                                        className="wcc-free-badge-container"
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                    >
                                        <div className="wcc-free-glowing-glow" />
                                        <div className="wcc-free-badge-card">
                                            <div className="wcc-free-sparkle-shine" />
                                            <div className="wcc-free-tag">
                                                <span className="wcc-free-dot-pulse" />
                                                عرض محدود
                                            </div>
                                            <h2 className="wcc-free-text-gradient">مجانية بالكامل</h2>
                                            <p className="wcc-free-subtext">احصل على وصول كامل لجميع الدروس والاختبارات مجاناً</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <div className="wcc-price-rows">
                                        <div className="wcc-price-row">
                                            <div className="lbl"><FontAwesomeIcon icon={faMoneyBill} /> السعر الأصلي:</div>
                                            <div className="val">{course.price} ج.م</div>
                                        </div>
                                        <div className="wcc-price-row">
                                            <div className="lbl"> قيمة الخصم:</div>
                                            <div className="val is-discount">-{course.discount} ج.م</div>
                                        </div>
                                        <div className="wcc-price-total">
                                            <div className="lbl"><FontAwesomeIcon icon={faShieldAlt} /> السعر النهائي:</div>
                                            <div className="val">{(course.price - course.discount).toLocaleString()} ج.م</div>
                                        </div>
                                    </div>
                                )}

                                {course.subscriptions_count > 0 && (
                                    <motion.div
                                        className="wcc-reserved-hint"
                                        initial={{ opacity: 0, scale: 0.85, y: 15 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <div className="wcc-reserved-shimmer" />
                                        <div className="wcc-reserved-content">
                                            <div className="wcc-reserved-avatars">
                                                <div className="wcc-mini-avatar"><FontAwesomeIcon icon={faUser} /></div>
                                                <div className="wcc-mini-avatar"><FontAwesomeIcon icon={faUser} /></div>
                                                <div className="wcc-mini-avatar"><FontAwesomeIcon icon={faUser} /></div>
                                            </div>
                                            <div className="wcc-reserved-info">
                                                <div className="wcc-reserved-count">
                                                    <span className="wcc-reserved-live-dot" />
                                                    <strong>{course.subscriptions_count}</strong> طالب مسجل
                                                </div>
                                                <span className="wcc-reserved-sub">سجّل الآن قبل نفاد الأماكن!</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                <button className="wcc-btn-enroll" onClick={handleRegisterNow} disabled={registering || isPreview}>
                                    {registering ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPlay} /><span>سجل في الدورة الآن</span></>}
                                </button>

                                {!storageHelper.getItem('token') && (
                                    <div className="wcc-login-link-under-enroll">
                                        <span>مشترك بالفعل في المنصة؟ </span>
                                        <span className="wcc-login-link-action" onClick={() => navigate('/signin')}>
                                            سجل دخولك الآن
                                        </span>
                                    </div>
                                )}

                                <div className="wcc-features-list">
                                    <div className="wcc-feat-item"><FontAwesomeIcon icon={faCheckCircle} /><span>وصول دائم للمحتوى</span></div>
                                    {course.is_certified && <div className="wcc-feat-item"><FontAwesomeIcon icon={faCheckCircle} /><span>شهادة إتمام معتمدة</span></div>}
                                    <div className="wcc-feat-item"><FontAwesomeIcon icon={faCheckCircle} /><span>دعم فني وتواصل مباشر</span></div>
                                    <div className="wcc-feat-item"><FontAwesomeIcon icon={faCheckCircle} /><span>تحديثات مستمرة للمنهج</span></div>
                                </div>

                                <div className="wcc-sidebar-promo">
                                    <FontAwesomeIcon icon={faTrophy} />
                                    <p>انضم لأكثر من 1000 طالب متفوق على المنصة</p>
                                </div>
                            </motion.div>
                        </aside>

                    </div>
                </div>
            </div>

            <Footer />

            <style jsx>{`
                .wcc-root {
                    font-family: "Zain", sans-serif;
                    background: #fff;
                    min-height: 100vh;
                    direction: rtl;
                }

                .wcc-container { max-width: 1300px; margin: 0 auto; padding: 0 24px; }

                /* ── Hero ── */
                .wcc-hero {
                    position: relative; height: 100vh; min-height: 550px;
                    display: flex; align-items: center; overflow: hidden;
                }
                .wcc-hero-bg { position: absolute; inset: 0; z-index: 1; }
                .wcc-hero-bg img { width: 100%; height: 100%; object-fit: cover; filter: brightness(0.6); }
                .wcc-hero-overlay {
                    position: absolute; inset: 0;
                    background: linear-gradient(0deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.4) 100%);
                }
                
                .wcc-hero-content { position: relative; z-index: 5; color: #fff; max-width: 900px; }
                .wcc-btn-back {
                    display: inline-flex; align-items: center; gap: 10px;
                    background: rgba(255,255,255,0.12); backdrop-filter: blur(12px);
                    border: 1px solid rgba(255,255,255,0.2); border-radius: 50px;
                    padding: 10px 24px; color: #fff; font-family: inherit; font-weight: 800;
                    margin-bottom: 48px; cursor: pointer; transition: 0.3s;
                }
                .wcc-btn-back:hover { background: rgba(255,255,255,0.2); transform: translateX(-5px); }

                .wcc-cat-tag {
                    display: inline-flex; align-items: center; gap: 8px;
                    background: #6366f1; color: #fff; padding: 8px 20px;
                    border-radius: 50px; font-weight: 900; font-size: 0.81rem; margin-bottom: 24px;
                }

                .wcc-outside-title-box {
                    margin-bottom: 32px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid rgba(15, 23, 42, 0.06);
                }
                .wcc-outside-title {
                    font-size: clamp(1.7rem, 2.98vw, 2.55rem);
                    font-weight: 900;
                    color: #0f172a;
                    line-height: 1.3;
                    margin: 0;
                }

                .wcc-hero-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; }
                .wcc-meta-pill {
                    display: inline-flex; align-items: center; gap: 10px;
                    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
                    padding: 12px 24px; border-radius: 16px; font-weight: 800; font-size: 0.94rem;
                }
                .wcc-meta-pill svg { color: #6366f1; }
                .wcc-btn-share-hero {
                    width: 54px; height: 54px; border-radius: 16px; border: none;
                    background: #fff; color: #0f172a; cursor: pointer; font-size: 1.02rem;
                    display: flex; align-items: center; justify-content: center; transition: 0.3s;
                }
                .wcc-hero-play-wrapper {
                    display: flex; align-items: center; gap: 24px; margin-bottom: 32px;
                }
                .wcc-huge-play-btn {
                    position: relative; width: 80px; height: 80px; border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #4f46e5); border: none;
                    display: flex; align-items: center; justify-content: center;
                    color: #fff; font-size: 1.53rem; cursor: pointer; z-index: 10;
                    box-shadow: 0 10px 30px rgba(99,102,241,0.4); transition: transform 0.3s;
                }
                .wcc-huge-play-btn:hover { transform: scale(1.08); }
                .wcc-play-icon-inner { margin-right: -4px; /* to center the play triangle visually */ }
                .wcc-play-pulse {
                    position: absolute; inset: 0; border-radius: 50%;
                    background: #6366f1; z-index: -1;
                    animation: playPulse 2s infinite ease-out;
                }
                @keyframes playPulse {
                    0% { transform: scale(1); opacity: 0.8; }
                    100% { transform: scale(1.6); opacity: 0; }
                }
                .wcc-play-text { font-size: 1.36rem; font-weight: 900; color: #fff; text-shadow: 0 2px 10px rgba(0,0,0,0.5); }

                /* ── Content Grid ── */
                .wcc-main-wrap { padding: 80px 0 120px; background: #fff; }
                .wcc-grid { display: grid; grid-template-columns: 1fr 400px; gap: 64px; align-items: flex-start; }

                /* Stats Bar */
                .wcc-stats-bar {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 20px;
                    margin-bottom: 64px;
                }
                .wcc-stat-card {
                    background: #fff;
                    border: 1.5px solid rgba(15,23,42,0.06);
                    border-radius: 24px;
                    padding: 24px 28px;
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    min-width: 220px;
                    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.02);
                }
                .wcc-stat-icon { width: 52px; height: 52px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 1.27rem; flex-shrink: 0; }
                .wcc-stat-icon.is-gold { background: #fffbeb; color: #6366f1; }
                .wcc-stat-icon.is-purple { background: #f3e8ff; color: #9333ea; }
                .wcc-stat-icon.is-indigo { background: #f5f3ff; color: #6366f1; }
                .wcc-stat-icon.is-emerald { background: #ecfdf5; color: #10b981; }
                .wcc-stat-info .val { font-size: 1.53rem; font-weight: 900; color: #0f172a; line-height: 1.2; }
                .wcc-stat-info .lbl { font-size: 0.88rem; color: #64748b; font-weight: 800; margin-top: 4px; white-space: nowrap; }

                /* Sections */
                .wcc-section { margin-bottom: 80px; }
                .wcc-eyebrow {
                    display: inline-flex; align-items: center; gap: 10px;
                    color: #6366f1; font-weight: 800; font-size: 0.72rem;
                    letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 16px;
                }
                .wcc-eyebrow-dot { width: 8px; height: 8px; border-radius: 50%; background: #6366f1; }
                .wcc-sec-title { font-size: 1.87rem; font-weight: 900; color: #0f172a; margin: 0 0 32px; }
                .wcc-sec-title em { font-style: normal; color: #6366f1; }

                .wcc-card-desc {
                    background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 32px;
                    padding: 48px; position: relative;
                }
                .wcc-quote-icon { position: absolute; top: 20px; right: 20px; font-size: 2.55rem; color: #6366f1; opacity: 0.05; }
                .wcc-html-content { font-size: 0.98rem; color: #475569; line-height: 1.9; font-weight: 500; }
                .wcc-html-content p { margin: 0 0 16px; }
                .wcc-html-content h1, .wcc-html-content h2, .wcc-html-content h3 { color: #0f172a; margin-top: 24px; margin-bottom: 12px; }
                .wcc-html-content ul, .wcc-html-content ol { padding-right: 24px; margin-bottom: 16px; }
                .wcc-html-content strong { color: #0f172a; }

                /* Curriculum Section */
                .wcc-curriculum-list {
                    display: flex; flex-direction: column; gap: 16px;
                }
                .wcc-curriculum-item {
                    background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 24px;
                    padding: 24px 32px; display: flex; flex-direction: column; align-items: stretch;
                    transition: all 0.3s ease; position: relative; overflow: hidden;
                }
                .wcc-curriculum-header {
                    display: flex; align-items: center; gap: 24px; cursor: pointer; width: 100%;
                }
                .wcc-curriculum-body {
                    padding-top: 16px; margin-right: 48px;
                }
                .wcc-curriculum-toggle {
                    width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
                    border-radius: 50%; background: #f8fafc; transition: all 0.3s ease;
                }
                .wcc-curriculum-item:hover {
                    border-color: #6366f1; box-shadow: 0 12px 30px rgba(99, 102, 241, 0.08);
                    transform: translateY(-3px);
                }
                .wcc-curriculum-item:hover .wcc-curriculum-toggle {
                    background: #e0e7ff; color: #6366f1;
                }
                .wcc-curriculum-num {
                    font-size: 1.87rem; font-weight: 900; color: rgba(99, 102, 241, 0.15);
                    font-variant-numeric: tabular-nums;
                    transition: color 0.3s ease;
                }
                .wcc-curriculum-item:hover .wcc-curriculum-num { color: #6366f1; }
                .wcc-curriculum-info { flex: 1; }
                .wcc-curriculum-title {
                    font-size: 1.06rem; font-weight: 900; color: #0f172a; margin: 0 0 8px;
                }
                .wcc-curriculum-desc {
                    font-size: 0.89rem; color: #475569; font-weight: 600; line-height: 1.6;
                    margin: 0 0 16px;
                }
                .wcc-curriculum-desc p { margin: 0; display: inline; }
                .wcc-curriculum-meta {
                    display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
                }
                .wcc-meta-pill.is-small {
                    padding: 6px 14px; font-size: 0.72rem; background: #f8fafc; color: #64748b;
                    border: 1px solid #e2e8f0; border-radius: 12px; margin: 0;
                }
                .wcc-meta-pill.is-small.is-attachment {
                    background: #f0fdf4; color: #16a34a; border-color: #bbf7d0;
                }
                .wcc-meta-pill.is-small svg { color: inherit; }
                .wcc-meta-pill.is-small svg { color: inherit; }

                /* Comments */
                .wcc-sec-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
                .wcc-comments-count { font-weight: 900; color: #64748b; font-size: 0.94rem; }

                .wcc-comment-form-wrap {
                    background: #f8fafc; border: 1.5px solid #f1f5f9; border-radius: 28px; padding: 32px; margin-bottom: 48px;
                }
                .wcc-comment-form textarea {
                    width: 100%; border: 1.5px solid #e2e8f0; border-radius: 18px; padding: 20px;
                    font-family: inherit; font-size: 0.94rem; font-weight: 600; margin-bottom: 20px;
                    resize: none; outline: none; transition: 0.3s;
                }
                .wcc-comment-form textarea:focus { border-color: #6366f1; box-shadow: 0 8px 24px rgba(99, 102, 241, 0.06); }
                .wcc-comment-form button {
                    height: 54px; padding: 0 32px; background: #6366f1; color: #fff; border: none;
                    border-radius: 16px; font-family: inherit; font-weight: 900; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 12px; transition: 0.3s;
                }
                .wcc-comment-form button:hover { background: #4f46e5; transform: scale(1.02); }

                .wcc-comment-card {
                    background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 24px;
                    padding: 24px; margin-bottom: 20px;
                }
                .wcc-cmt-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
                .wcc-user-info { display: flex; align-items: center; gap: 16px; }
                .wcc-avatar { width: 50px; height: 50px; border-radius: 50%; background: #f1f5f9; overflow: hidden; display: flex; align-items: center; justify-content: center; font-size: 1.02rem; color: #94a3b8; }
                .wcc-avatar img { width: 100%; height: 100%; object-fit: cover; }
                .wcc-user-meta .name { font-weight: 900; color: #0f172a; font-size: 0.94rem; }
                .wcc-user-meta .date { font-size: 0.72rem; color: #94a3b8; font-weight: 700; margin-top: 2px; }
                .wcc-btn-del { width: 36px; height: 36px; border-radius: 10px; border: none; background: #fff1f2; color: #e11d48; cursor: pointer; }
                .wcc-cmt-body p { font-size: 0.94rem; color: #475569; line-height: 1.7; font-weight: 500; }
                .wcc-empty-comments { text-align: center; padding: 60px 0; color: #cbd5e1; }
                .wcc-empty-comments svg { font-size: 2.98rem; margin-bottom: 20px; opacity: 0.3; }

                /* ── Sidebar ── */
                .wcc-sidebar-col { position: sticky; top: 140px; }
                .wcc-pricing-card {
                    background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 40px;
                    padding: 40px; box-shadow: 0 20px 50px rgba(0,0,0,0.03);
                }
                .wcc-pricing-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
                .wcc-pricing-head h3 { font-size: 1.27rem; font-weight: 900; color: #0f172a; }
                .wcc-price-badge { width: 44px; height: 44px; border-radius: 12px; background: #fff9db; color: #6366f1; display: flex; align-items: center; justify-content: center; font-size: 1.02rem; }

                .wcc-price-rows { margin-bottom: 32px; }
                .wcc-price-row { display: flex; justify-content: space-between; padding: 14px 0; border-bottom: 1px dashed #f1f5f9; }
                .wcc-price-row .lbl { color: #64748b; font-weight: 800; font-size: 0.89rem; display: flex; align-items: center; gap: 10px; }
                .wcc-price-row .lbl svg { width: 20px; color: #94a3b8; }
                .wcc-price-row .val { font-weight: 900; color: #1e293b; font-size: 0.98rem; }
                .wcc-price-row .val.is-discount { color: #ef4444; }
                .wcc-price-total { display: flex; justify-content: space-between; padding-top: 24px; align-items: baseline; }
                .wcc-price-total .lbl { font-size: 1.02rem; font-weight: 900; color: #0f172a; display: flex; align-items: center; gap: 12px; }
                .wcc-price-total .lbl svg { color: #6366f1; }
                .wcc-price-total .val { font-size: 2.04rem; font-weight: 900; color: #6366f1; }

                .wcc-btn-enroll {
                    width: 100%; height: 64px; background: #6366f1; color: #fff; border: none;
                    border-radius: 20px; font-family: inherit; font-weight: 900; font-size: 1.06rem;
                    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 14px;
                    box-shadow: 0 15px 30px rgba(99, 102, 241, 0.2); transition: 0.3s; margin-bottom: 24px;
                }
                .wcc-btn-enroll:hover { background: #4f46e5; transform: translateY(-3px); }

                .wcc-login-link-under-enroll {
                    text-align: center;
                    margin-top: -8px;
                    margin-bottom: 32px;
                    font-size: 0.94rem;
                    color: #64748b;
                    font-weight: 800;
                }
                .wcc-login-link-action {
                    color: #6366f1;
                    cursor: pointer;
                    text-decoration: underline;
                    transition: color 0.2s ease;
                    margin-right: 4px;
                }
                .wcc-login-link-action:hover {
                    color: #4f46e5;
                }

                /* ── Reserved Hint ── */
                .wcc-reserved-hint {
                    position: relative;
                    overflow: hidden;
                    background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 40%, #ecfdf5 100%);
                    border: 1.5px solid #bbf7d0;
                    border-radius: 20px;
                    padding: 18px 22px;
                    margin-bottom: 20px;
                }
                .wcc-reserved-shimmer {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.7) 50%, transparent 60%);
                    animation: wcc-shimmer 3s ease-in-out infinite;
                }
                @keyframes wcc-shimmer {
                    0% { transform: translateX(100%); }
                    100% { transform: translateX(-100%); }
                }
                .wcc-reserved-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .wcc-reserved-avatars {
                    display: flex;
                    flex-direction: row-reverse;
                }
                .wcc-mini-avatar {
                    width: 34px; height: 34px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    color: #fff;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.59rem;
                    border: 2.5px solid #ecfdf5;
                    margin-right: -10px;
                    box-shadow: 0 2px 8px rgba(99,102,241,0.25);
                }
                .wcc-mini-avatar:first-child { margin-right: 0; }
                .wcc-reserved-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .wcc-reserved-count {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.89rem;
                    font-weight: 800;
                    color: #166534;
                }
                .wcc-reserved-count strong {
                    font-size: 1.06rem;
                    font-weight: 900;
                    color: #15803d;
                }
                .wcc-reserved-live-dot {
                    width: 8px; height: 8px;
                    border-radius: 50%;
                    background: #22c55e;
                    animation: wcc-live-pulse 1.8s ease-in-out infinite;
                    flex-shrink: 0;
                }
                @keyframes wcc-live-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6); }
                    50% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
                }
                .wcc-reserved-sub {
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #16a34a;
                    opacity: 0.85;
                }

                .wcc-features-list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 32px; }
                .wcc-feat-item { display: flex; align-items: center; gap: 12px; font-weight: 800; color: #475569; font-size: 0.89rem; }
                .wcc-feat-item svg { color: #10b981; }

                .wcc-sidebar-promo {
                    background: #fdf4ff; border-radius: 20px; padding: 24px; text-align: center; color: #86198f;
                }
                .wcc-sidebar-promo svg { font-size: 1.87rem; margin-bottom: 12px; }
                .wcc-sidebar-promo p { font-weight: 900; font-size: 0.85rem; line-height: 1.5; }

                /* ── Glowing Free Badge ── */
                .wcc-free-badge-container {
                    position: relative;
                    margin-bottom: 32px;
                    padding: 4px;
                    border-radius: 24px;
                    background: linear-gradient(135deg, #7a6ad8 0%, #10b981 100%);
                    overflow: hidden;
                    box-shadow: 0 10px 30px rgba(122, 106, 216, 0.2);
                }
                .wcc-free-glowing-glow {
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: conic-gradient(from 0deg, transparent 20%, #7a6ad8, #10b981, transparent 80%);
                    animation: rotate-glow 4s linear infinite;
                    z-index: 1;
                }
                @keyframes rotate-glow {
                    100% { transform: rotate(360deg); }
                }
                .wcc-free-badge-card {
                    position: relative;
                    z-index: 2;
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 32px 24px;
                    text-align: center;
                    overflow: hidden;
                }
                .wcc-free-sparkle-shine {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.6), transparent);
                    transform: translateX(-100%) skewX(-30deg);
                    animation: shine-sweep 3s ease-in-out infinite;
                }
                @keyframes shine-sweep {
                    0% { transform: translateX(-100%) skewX(-30deg); }
                    30% { transform: translateX(100%) skewX(-30deg); }
                    100% { transform: translateX(100%) skewX(-30deg); }
                }
                .wcc-free-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #ecfdf5;
                    color: #059669;
                    font-size: 0.77rem;
                    font-weight: 800;
                    padding: 6px 16px;
                    border-radius: 50px;
                    margin-bottom: 16px;
                    border: 1px solid #a7f3d0;
                }
                .wcc-free-dot-pulse {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #10b981;
                    animation: free-pulse 1.5s ease-in-out infinite;
                }
                @keyframes free-pulse {
                    0% { transform: scale(0.9); opacity: 0.6; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
                    50% { transform: scale(1.1); opacity: 1; box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
                    100% { transform: scale(0.9); opacity: 0.6; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
                }
                .wcc-free-text-gradient {
                    font-size: 1.87rem;
                    font-weight: 900;
                    background: linear-gradient(135deg, #7a6ad8 0%, #10b981 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    margin: 0 0 10px;
                    letter-spacing: -0.5px;
                    animation: scale-up-down 2.5s ease-in-out infinite alternate;
                }
                @keyframes scale-up-down {
                    0% { transform: scale(0.98); }
                    100% { transform: scale(1.03); }
                }
                .wcc-free-subtext {
                    font-size: 0.89rem;
                    color: #64748b;
                    font-weight: 700;
                    line-height: 1.6;
                    margin: 0;
                }

                .wcc-loader-box { height: 70vh; display: flex; align-items: center; justify-content: center; }
                .wcc-error-box { height: 70vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 24px; }
                .wcc-error-box button { padding: 12px 32px; background: #6366f1; color: #fff; border: none; border-radius: 50px; font-weight: 900; cursor: pointer; }

                .wcc-root button:disabled, .wcc-root input:disabled, .wcc-root textarea:disabled {
                    opacity: 0.5 !important;
                    cursor: not-allowed !important;
                    pointer-events: none !important;
                }

                .wcc-preview-mode header,
                .wcc-preview-mode .main-header-area,
                .wcc-preview-mode .header-area,
                .wcc-preview-mode .sub-header-area,
                .wcc-preview-mode nav {
                    display: none !important;
                }

                .wcc-preview-mode .wcc-hero {
                    padding-top: 40px !important;
                    min-height: unset !important;
                }

                @media (max-width: 1100px) {
                    .wcc-grid { grid-template-columns: 1fr; }
                    .wcc-sidebar-col { position: static; max-width: 500px; margin: 0 auto; }
                    .wcc-hero { height: auto; padding: 160px 0 80px; min-height: unset; }
                    .wcc-title { font-size: 2.38rem; }
                    .wcc-stats-bar { grid-template-columns: repeat(2, 1fr); }
                }

                @media (max-width: 768px) {
                    .wcc-stats-bar { grid-template-columns: 1fr; }
                    .wcc-stat-card { padding: 24px; }
                    .wcc-card-desc { padding: 32px 24px; }
                    .wcc-card-desc p { font-size: 0.98rem; }
                    .wcc-title { font-size: 1.87rem; }
                    .wcc-pricing-card { padding: 30px 24px; }
                    .wcc-curriculum-item { padding: 20px; }
                    .wcc-curriculum-header { gap: 16px; align-items: flex-start; }
                    .wcc-curriculum-body { margin-right: 0; padding-top: 12px; }
                }
            `}</style>

            <DynamicModal
                isOpen={showFreeSubscribeModal}
                onClose={() => setShowFreeSubscribeModal(false)}
                formConfig={freeSubscribeFormConfig}
                title="تسجيل حساب والاشتراك في الدورة المجانية"
                submitButtonText="سجل واشترك الآن"
                onSubmit={handleFreeSubscribeSubmit}
                direction="rtl"
            />

            {/* Intro Video Modal */}
            <AnimatePresence>
                {showIntroVideo && course?.intro_video_url && (
                    <motion.div
                        className="wcc-video-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(15, 23, 42, 0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <VideoViewerV2
                            url={getFullFileUrl(course.intro_video_url)}
                            onClose={() => setShowIntroVideo(false)}
                            autoPlay
                            style={{ width: '90%', height: '85%', maxWidth: '1200px' }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default WebsiteCourseContent;